import { useEffect, useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { getDeliveryChallans } from '../services/api'
import { formatCurrency, formatDate } from '../lib/formatters'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import StatusBadge from '../components/ui/StatusBadge'
import Avatar from '../components/ui/Avatar'
import EmptyState from '../components/ui/EmptyState'

const STATUSES = ['ALL', 'DRAFT', 'APPROVED', 'DISPATCHED', 'DELIVERED']

export default function DeliveryChallans() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [allDCs, setAllDCs] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const status = searchParams.get('status') || 'ALL'

  useEffect(() => {
    setLoading(true)
    getDeliveryChallans({ take: 9999 })
      .then((res) => setAllDCs(res.data.deliveryChallans || []))
      .catch(() => setAllDCs([]))
      .finally(() => setLoading(false))
  }, [])

  const statusCounts = useMemo(() => {
    const counts = { ALL: allDCs.length }
    STATUSES.forEach((s) => { if (s !== 'ALL') counts[s] = 0 })
    allDCs.forEach((dc) => {
      if (counts[dc.status] !== undefined) counts[dc.status]++
    })
    return counts
  }, [allDCs])

  const filtered = useMemo(() => {
    let list = allDCs
    if (status !== 'ALL') list = list.filter((dc) => dc.status === status)
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter((dc) =>
        (dc.number || '').toLowerCase().includes(q) ||
        (dc.customer?.name || '').toLowerCase().includes(q)
      )
    }
    return list
  }, [allDCs, status, search])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } = useSort(filtered, {
    defaultSort: 'date', defaultDir: 'desc', pageSize: 25, storageKey: 'deliveryChallans',
  })

  const setStatus = (s) => {
    const p = new URLSearchParams(searchParams)
    if (s === 'ALL') p.delete('status'); else p.set('status', s)
    setSearchParams(p)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Delivery Challans</h1>
        <Link to="/delivery-challans/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Delivery Challan
        </Link>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" placeholder="Search delivery challans..." className="input pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 flex-wrap">
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${status === s ? 'bg-brand-100 text-brand-700' : 'text-gray-500 hover:bg-gray-100'}`}>
              {s}<span className={`ml-1 ${status === s ? 'text-brand-500' : 'text-gray-400'}`}>{statusCounts[s] ?? 0}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-4">{[...Array(5)].map((_, i) => (<div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />))}</div>
        ) : filtered.length === 0 ? (
          <EmptyState type="delivery challans" message={status !== 'ALL' || search ? 'No delivery challans match your filters.' : 'No delivery challans yet. Create your first one.'} actionLabel={status === 'ALL' && !search ? 'New Delivery Challan' : undefined} actionTo={status === 'ALL' && !search ? '/delivery-challans/new' : undefined} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b">
                    <SortHeader label="DC #" sortKey="number" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Customer" sortKey="customer.name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Date" sortKey="date" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Amount" sortKey="total" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                    <SortHeader label="Status" sortKey="status" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((dc) => (
                    <tr key={dc.id} className="border-b last:border-0 hover:bg-gray-50/70 transition-colors">
                      <td className="px-4 py-3"><Link to={`/delivery-challans/${dc.id}`} className="text-brand-600 hover:underline font-medium">{dc.number || 'DRAFT'}</Link></td>
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar name={dc.customer?.name} /><span className="text-gray-600">{dc.customer?.name || '—'}</span></div></td>
                      <td className="px-4 py-3 text-gray-500">{formatDate(dc.date)}</td>
                      <td className="px-4 py-3 text-right">{formatCurrency(dc.total, dc.currency)}</td>
                      <td className="px-4 py-3"><StatusBadge status={dc.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
